import { isRecord } from '@/utils/objects';
import type { ConditionNode, SegmentNode } from '../types';
import { toNumber, toString } from './parse-helpers';

/**
 * Parse segment conditions.
 * Handles: { segment: { id, inverse } }
 *          { segment_id: number, inverse }
 *          { type: 'segment', segment_id }
 *          { type: 'segments_negatable', segments: [...], negate }
 */
export function parseSegment(raw: Record<string, unknown>): ConditionNode | null {
  // Ember format: { segment: { id, inverse } }
  if ('segment' in raw && isRecord(raw.segment)) {
    const seg = raw.segment;
    const id = toNumber(seg.id);
    if (id == null) return null;
    return {
      type: 'segment',
      segmentId: id,
      inverse: seg.inverse === true,
      segmentName: toString(seg.name ?? raw.segment_name),
    };
  }

  // segments_negatable: { type: 'segments_negatable', segments: [1, 2], negate }
  // Multiple segments → group of segment nodes so no data is dropped
  if (raw.type === 'segments_negatable' && Array.isArray(raw.segments)) {
    const segments = raw.segments;
    if (segments.length === 0) return null;
    const inverse = raw.negate === true;
    const names = isRecord(raw.segment_names) ? raw.segment_names : undefined;

    const nodes: SegmentNode[] = [];
    for (const segment of segments) {
      const id = toNumber(segment);
      if (id == null) continue;
      nodes.push({
        type: 'segment',
        segmentId: id,
        inverse,
        segmentName: names != null ? toString(names[String(id)]) : undefined,
      });
    }

    if (nodes.length === 0) return null;
    if (nodes.length === 1) return nodes[0];
    return { type: 'group', operator: inverse ? 'and' : 'or', children: nodes };
  }

  // { segment_id: number }
  if ('segment_id' in raw && typeof raw.segment_id === 'number' && raw.segment_id > 0) {
    return {
      type: 'segment',
      segmentId: raw.segment_id,
      inverse: raw.inverse === true,
      segmentName: toString(raw.segment_name),
    };
  }

  // { type: 'segment' } or { type: 'segments' }
  if (raw.type === 'segment' || raw.type === 'segments') {
    const id = toNumber(raw.segment_id);
    if (id == null || id <= 0) return null;
    return {
      type: 'segment',
      segmentId: id,
      inverse: raw.inverse === true,
      segmentName: toString(raw.segment_name),
    };
  }

  return null;
}
